import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Outlet, useParams } from "react-router-dom";
import styled from "styled-components";
import DashboardDataProvider from "../../components/dashboardDataProvider";
import PrimaryFilterHelper from "../../components/primaryFilterHelper";
import { RootState } from "../../state/store";
import HappeningsProvider from "../../features/happenings/provider";
import { WHITELISTED_DASHBOARD_PATHS } from "../../helpers/constants";
import { RouteParams } from "../../types/routes";

const Layout = () => {
  const { company_details } = useSelector((state: RootState) => state.company);
  const { locations } = useSelector((state: RootState) => state);
  const { dashboards } = useSelector((state: RootState) => state.dashboard);

  const routeMatch = useParams<RouteParams>();
  const currentDashboard = routeMatch.dashboard || "";

  // Memoized values for better performance
  const accessibleDashboardRoutes = useMemo(
    () => dashboards.map((dashboard) => dashboard.api_path),
    [dashboards]
  );

  const isMaintenance = useMemo(
    () => company_details?.maintenance_mode?.enabled || false,
    [company_details?.maintenance_mode?.enabled]
  );

  const showContent = useMemo(() => {
    return !!(company_details?.company_id && locations.length > 0);
  }, [company_details?.company_id, locations.length]);

  const shouldRenderDashboardContent = useMemo(() => {
    const isNotWhitelisted =
      !WHITELISTED_DASHBOARD_PATHS.includes(currentDashboard);
    const isAccessibleDashboard =
      accessibleDashboardRoutes.includes(currentDashboard);

    return isNotWhitelisted && isAccessibleDashboard;
  }, [currentDashboard, accessibleDashboardRoutes]);

  // Early return for whitelisted paths or non-accessible dashboards
  if (!shouldRenderDashboardContent) {
    return null;
  }

  return (
    <PrimaryFilterHelper>
      <HappeningsProvider>
        <DashboardDataProvider dashboard={currentDashboard}>
          <MaintenanceSpacing isMaintenance={isMaintenance} />
          <LayoutWrapper>{showContent ? <Outlet /> : null}</LayoutWrapper>
        </DashboardDataProvider>
      </HappeningsProvider>
    </PrimaryFilterHelper>
  );
};

export default Layout;

const LayoutWrapper = styled.div``;

const MaintenanceSpacing = styled.div<{ isMaintenance: boolean }>`
  height: ${({ isMaintenance }) => (isMaintenance ? "120px" : "60px")};
`;
